<template>
  <div>
    <!-- Section with Full-Screen Image -->
    <section id="home" class="relative w-full h-screen bg-cover bg-center" :style="{ backgroundImage: 'url(' + heroImage + ')' }">
      <div class="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
      <div class="relative z-10 flex flex-col items-center justify-center h-full px-4 text-center">
        <h1 class="text-white text-4xl md:text-6xl font-bold mb-4 animate-fade-in">{{ $t('hero.title') }}</h1>
        <p class="text-white/80 text-xl md:text-2xl mb-8 animate-fade-in-delay">{{ $t('hero.subtitle') }}</p>
        <button @click="scrollToOffers" class="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg animate-fade-in-delay-2">
          {{ $t('hero.cta') }}
        </button>
      </div>
      <!-- Scroll indicator -->
      <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <svg class="w-8 h-8 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </div>
    </section>

    <!-- Company Explanation Section -->
    <section id="about" class="bg-gray-100 py-16 px-4">
      <div class="max-w-7xl mx-auto fade-in-section">
        <h2 class="text-3xl sm:text-4xl font-bold text-center text-teal-800 mb-8">{{ $t('about.title') }}</h2>
        <p class="text-lg sm:text-xl text-center text-gray-700 leading-relaxed max-w-4xl mx-auto">
          {{ $t('about.description') }}
        </p>
      </div>
    </section>

    <!-- Fleet Section -->
    <FleetSection />

    <!-- Carousel Section -->
    <section id="offers" class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 fade-in-section">
        <h2 class="text-3xl sm:text-4xl font-bold text-center text-teal-800 mb-8">{{ $t('offers.title') }}</h2>
        <CarouselComponent />
      </div>
    </section>

    <!-- Map and Opening Hours Section -->
    <section id="contact" class="w-full bg-gray-50 py-16">
      <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 px-4">
        <!-- Map on the Left -->
        <div class="w-full h-80 md:h-96 border rounded-xl shadow-lg overflow-hidden fade-in-section">
          <iframe
            class="w-full h-full"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1991.3095818950371!2d10.426118663437792!3d45.1032904281422!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47804ae723b6ba3f%3A0x1e224bb400667b96!2sF.lli%20Pizzoni%20Snc!5e0!3m2!1sit!2sit!4v1726220166594!5m2!1sit!2sit"
            frameborder="0"
            style="border:0;"
            allowfullscreen=""
            aria-hidden="false"
            tabindex="0"
          ></iframe>
        </div>

        <!-- Opening Hours on the Right -->
        <div class="w-full h-full flex flex-col justify-center px-6 py-8 bg-white border rounded-xl shadow-lg fade-in-section">
          <h2 class="text-2xl font-bold text-teal-800 mb-6">{{ $t('contact.title') }}</h2>
          <ul class="text-lg text-gray-700 space-y-3">
            <li class="flex justify-between border-b border-gray-100 pb-2"><strong>{{ $t('contact.monday') }}:</strong> <span>9:00 - 13:00 / 14:00 - 16:00</span></li>
            <li class="flex justify-between border-b border-gray-100 pb-2"><strong>{{ $t('contact.tuesday') }}:</strong> <span>9:00 - 13:00 / 14:00 - 16:00</span></li>
            <li class="flex justify-between border-b border-gray-100 pb-2"><strong>{{ $t('contact.wednesday') }}:</strong> <span>9:00 - 13:00 / 14:00 - 16:00</span></li>
            <li class="flex justify-between border-b border-gray-100 pb-2"><strong>{{ $t('contact.thursday') }}:</strong> <span>9:00 - 13:00 / 14:00 - 16:00</span></li>
            <li class="flex justify-between border-b border-gray-100 pb-2"><strong>{{ $t('contact.friday') }}:</strong> <span>9:00 - 13:00 / 14:00 - 16:00</span></li>
            <li class="flex justify-between border-b border-gray-100 pb-2"><strong>{{ $t('contact.saturday') }}:</strong> <span>9:00 - 13:00 / 14:00 - 16:00</span></li>
            <li class="flex justify-between text-red-600"><strong>{{ $t('contact.sunday') }}:</strong> <span>{{ $t('contact.closed') }}</span></li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import CarouselComponent from '@/components/Carousel.vue';
import FleetSection from '@/components/FleetSection.vue';

export default {
  name: 'HomePage',
  components: {
    CarouselComponent,
    FleetSection,
  },
  data() {
    return {
      heroImage: require('@/assets/homepage/home_page.jpeg')
    };
  },
  mounted() {
    this.initScrollAnimations();
  },
  methods: {
    scrollToOffers() {
      document.getElementById('offers').scrollIntoView({ behavior: 'smooth' });
    },
    initScrollAnimations() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      document.querySelectorAll('.fade-in-section').forEach(el => {
        observer.observe(el);
      });
    }
  }
};
</script>

<style scoped>
/* Hero animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 1s ease-out forwards;
}

.animate-fade-in-delay {
  opacity: 0;
  animation: fadeIn 1s ease-out 0.3s forwards;
}

.animate-fade-in-delay-2 {
  opacity: 0;
  animation: fadeIn 1s ease-out 0.6s forwards;
}
</style>
